import { sleep } from "@Components/LeadFormModal/utils";
import Meta from "./helper";
import { AppState } from "@Store/index";
import config from "config/index";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import { isAppWebViewFromAppData } from "hooks/useIsApp";
import useWhitelabel from "hooks/useWhitelabel";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { shallowEqual, useSelector } from "react-redux";
import { useLocation } from "react-router";
import getShareImage from "./utils/getShareImage";
import getImagePath from "@Utils/getImagePath";
// import generateHreflangUrls from "@Utils/generateHreflangUrls"; // temporarily unused — re-enable with hreflang render below
import { generateCanonicalUrl } from "@Utils/generateHreflangUrls";
import { isNoindexLang } from "@Utils/languageCore";

const imageUrl = `${config.IMAGE_STATIC_URL}/images`;

const getFaviconUrl = (isWhitelabel: boolean, whitelabelConfig: any, size: string) => {
  if (isWhitelabel && whitelabelConfig?.domain?.fav_icon) {
    return getImagePath(whitelabelConfig.domain.fav_icon);
  }
  return `${imageUrl}/logo/favicon-${size}.png`;
};

const getAppleTouchIconUrl = (isWhitelabel: boolean, whitelabelConfig: any, size: string) => {
  if (isWhitelabel && whitelabelConfig?.domain?.fav_icon) {
    return getImagePath(whitelabelConfig.domain.fav_icon);
  }
  return `${imageUrl}/logo/apple-icon-${size}.png`;
};

export default function MetaConfigTags() {
  const { metaObj, nonce, userLanguage, isApp, appShell } = useSelector(
    ({ appData }: AppState) => ({
      metaObj: appData.metaObj,
      nonce: appData.nonce,
      userLanguage: appData.userLanguage,
      isApp: appData.isApp,
      appShell: appData.appShell,
    }),
    shallowEqual,
  );
  const isAppWebView = isAppWebViewFromAppData({ isApp, appShell });

  const indexableDomain = useSelector(
    ({ appData }: AppState) => appData.indexableDomain,
    shallowEqual,
  );
  const {
    whitelabelConfig,
    isWhitelabel,
    isWhitelabelSeoEnabled,
    whitelabelGoogleSiteVerificationId,
  } = useWhitelabel();

  const meta = Meta.build({ ...metaObj });
  const location = useLocation();
  const currentPath = location.pathname;
  const isEuLangNoindexPage = isNoindexLang(userLanguage);

  // Hreflang temporarily disabled site-wide (including en / x-default)
  // // Generate hreflang links for international SEO
  // // China app (.cn) should not render any hreflang annotations
  // const shouldRenderHreflang =
  //   !config.IS_CHINA_APP &&
  //   !config.IS_WHITELABEL_APP &&
  //   meta?.canonicalUrl &&
  //   !meta.excludeAlternateLinks &&
  //   !isEuLangNoindexPage;
  // const hreflangLinks = shouldRenderHreflang ? generateHreflangUrls(meta.canonicalUrl) : [];

  // Any non-English page on a whitelabel site is a translated variant of the English
  // page, so it must never be indexed — regardless of the partner's SEO settings.
  const isWhitelabelTranslatedPage =
    config.IS_WHITELABEL_APP && isWhitelabel && !!userLanguage && userLanguage !== "en";

  const isWhitelabelHomepage = config.IS_WHITELABEL_APP && isWhitelabel && currentPath === "/";
  // The whitelabel homepage stays indexable in production, but only in English. A
  // translated homepage loses that exemption and falls through to noindex below.
  const isIndexableWhitelabelPage =
    isWhitelabelHomepage && config.isProduction && !isWhitelabelTranslatedPage;
  // Noindex when the locale is an EU noindex language OR the page is a non-English
  // whitelabel page. The English production whitelabel homepage is the one exemption and
  // must still emit index,follow. Gate all robots branches on this combined flag so we
  // never skip every branch.
  const forceEuLangNoindex =
    (isEuLangNoindexPage || isWhitelabelTranslatedPage) && !isIndexableWhitelabelPage;
  const organizationSchemaData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `https://amberstudent.${config.IS_CHINA_APP ? "cn" : "com"}/#organization`,
    name: "amberstudent",
    url: config.IS_CHINA_APP ? "https://amberstudent.cn" : "https://amberstudent.com",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Pune, India",
      postalCode: "411014",
      streetAddress: "Core D, 6 Floor, Solitaire Business Hub, Viman Nagar",
    },
    email: "contact@amberstudent.com",
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+44 7456741634",
        contactType: "customer service",
      },
      {
        "@type": "ContactPoint",
        telephone: "+91 8035735724",
        contactType: "customer service",
      },
    ],
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `https://query.amberstudent.${
          config.IS_CHINA_APP ? "cn" : "com"
        }/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
    logo: `${imageUrl}/logo/amber_red_logo_small_1.png`,
    sameAs: [
      ...(config.IS_CHINA_APP
        ? []
        : [
            "https://facebook.com/amberstudent",
            "https://twitter.com/amberstudent_",
            "https://instagram.com/amberstudent",
            "https://www.youtube.com/channel/UCzm8GT3xWuXqQt9O7CFpqJw",
            "https://www.linkedin.com/company/amberstudent",
          ]),
      `https://amberstudent.${config.IS_CHINA_APP ? "cn" : "com"}/`,
    ],
  };

  const iosAppSchemaData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "amber: find student apartments",
    url: `https://amberstudent.${config.IS_CHINA_APP ? "cn" : "com"}/`,
    operatingSystem: "iOS",
    applicationCategory: "LifestyleApplication",
    description:
      "Amber makes it easy to discover safe, affordable, and comfortable student accommodation in over 250 cities worldwide. With 10,000+ properties across top university locations in the UK, USA, Australia, Canada, etc, amber connects you with the best student housing options. Download the amber app and secure your perfect student home today!",
    softwareVersion: "3.8.0",
    creator: {
      "@type": "Organization",
      name: "Amber Internet Solutions",
    },
    downloadUrl: "https://apps.apple.com/us/app/amber-find-student-apartments/id6447250749",
    screenshot: `${config.IMAGE_STATIC_URL}/images/amber.svg?auto=format`,
    offers: {
      "@type": "Offer",
      url: "https://apps.apple.com/us/app/amber-find-student-apartments/id6447250749",
      priceCurrency: "USD",
      price: "0.00",
    },
    // aggregateRating: {
    //   "@type": "AggregateRating",
    //   ratingValue: "4.7",
    //   reviewCount: "134",
    // },
  };

  const androidAppSchemaData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "amber: find student apartments",
    url: `https://amberstudent.${config.IS_CHINA_APP ? "cn" : "com"}/`,
    operatingSystem: "Android",
    applicationCategory: "LifestyleApplication",
    description:
      "Amber makes it easy to discover safe, affordable, and comfortable student accommodation in over 250 cities worldwide. With 10,000+ properties across top university locations in the UK, USA, Australia, Canada, etc, amber connects you with the best student accommodation options. Download the amber app and secure your perfect student apartment today!",
    softwareVersion: "3.7.1",
    creator: {
      "@type": "Organization",
      name: "Amber Internet Solutions",
    },
    downloadUrl: "https://play.google.com/store/apps/details?id=com.yourcompany.yourapp",
    screenshot: `${config.IMAGE_STATIC_URL}/images/amber.svg?auto=format`,
    offers: {
      "@type": "Offer",
      url: "https://play.google.com/store/apps/details?id=com.amberstudent&hl=en",
      priceCurrency: "USD",
      price: "0.00",
      priceSpecification: {
        "@type": "PriceSpecification",
        price: "0.00",
        priceCurrency: "USD",
      },
    },
    // aggregateRating: {
    //   "@type": "AggregateRating",
    //   ratingValue: "4.7",
    //   reviewCount: "658",
    // },
  };

  const isMiniProgram = useIsMiniProgram();
  function loadWeixinScript() {
    // eslint-disable-next-line consistent-return
    return new Promise((resolve, reject) => {
      if (typeof window === "undefined") return resolve(null);
      // @ts-ignore
      if (window.wx) return resolve(window.wx);
      // Check if script already exists
      const existing = document.querySelector('script[src="/weixin.js"]');
      if (existing) {
        // @ts-ignore
        if (existing?.readyState === "complete" || existing?.readyState === "loaded")
          // @ts-ignore
          return resolve(window.wx);

        // @ts-ignore
        existing.addEventListener("load", () => resolve(window.wx), { once: true });
        existing.addEventListener("error", reject, { once: true });
        // eslint-disable-next-line consistent-return
        return;
      }

      // Create and append new script
      const script = document.createElement("script");
      script.src = "/weixin.js";
      script.async = true;
      script.nonce = nonce;
      // @ts-ignore
      script.addEventListener("load", () => resolve(window.wx), { once: true });
      script.addEventListener("error", reject, { once: true });

      document.head.appendChild(script);
    });
  }

  useEffect(() => {
    const postMessageToMiniProgram = async () => {
      // @ts-ignore
      if (typeof window !== "undefined" && isMiniProgram && typeof wx !== "undefined") {
        await sleep(100); // time to change title as we are using SPA
        const titleHTML = document.getElementsByTagName("title")?.[0];
        if (titleHTML) {
          const type = "META_TITLE";
          const title = titleHTML.innerText;
          const image = getShareImage(currentPath) || meta.ogImage;

          // @ts-ignore
          wx.miniProgram.postMessage({
            data: [{ type, title, currentPath, imageUrl: image, timestamp: Date.now() }],
          });
        }
      }
    };
    if (typeof window !== "undefined" && isMiniProgram) {
      loadWeixinScript().then(() => {
        postMessageToMiniProgram();
      });
    }
  }, [currentPath, meta.ogTitle]);

  return (
    <Helmet>
      {/* <!-- Basic --> */}
      {!config.IS_CHINA_APP && <meta name="referrer" content="strict-origin-when-cross-origin" />}
      <meta charSet="utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta
        name="viewport"
        content="width=device-width,viewport-fit=cover,minimum-scale=1,initial-scale=1,maximum-scale=1"
      />

      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="p:domain_verify" content="a6dadc4c3844569d9a9c972b06a38d6b" />

      {/* <!-- Theme Color --> */}
      <meta name="theme-color" content="#222" />

      {/* <!-- preload icons font --> */}
      {/* <link rel='preload' as='font' type='font/woff2' crossOrigin href="<%= assetPath('amber-font-icon.woff2') %>" /> */}

      {/* <!-- Favicon --> */}
      <link
        rel="apple-touch-icon"
        sizes="57x57"
        href={getAppleTouchIconUrl(isWhitelabel, whitelabelConfig, "57x57")}
      />
      <link
        rel="apple-touch-icon"
        sizes="60x60"
        href={getAppleTouchIconUrl(isWhitelabel, whitelabelConfig, "60x60")}
      />
      <link
        rel="apple-touch-icon"
        sizes="72x72"
        href={getAppleTouchIconUrl(isWhitelabel, whitelabelConfig, "72x72")}
      />
      <link
        rel="apple-touch-icon"
        sizes="76x76"
        href={getAppleTouchIconUrl(isWhitelabel, whitelabelConfig, "76x76")}
      />
      <link
        rel="apple-touch-icon"
        sizes="114x114"
        href={getAppleTouchIconUrl(isWhitelabel, whitelabelConfig, "114x114")}
      />
      <link
        rel="apple-touch-icon"
        sizes="120x120"
        href={getAppleTouchIconUrl(isWhitelabel, whitelabelConfig, "120x120")}
      />
      <link
        rel="apple-touch-icon"
        sizes="144x144"
        href={getAppleTouchIconUrl(isWhitelabel, whitelabelConfig, "144x144")}
      />
      <link
        rel="apple-touch-icon"
        sizes="152x152"
        href={getAppleTouchIconUrl(isWhitelabel, whitelabelConfig, "152x152")}
      />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href={getAppleTouchIconUrl(isWhitelabel, whitelabelConfig, "180x180")}
      />
      <link
        rel="icon"
        type="image/png"
        sizes="192x192"
        href={getFaviconUrl(isWhitelabel, whitelabelConfig, "192x192")}
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href={getFaviconUrl(isWhitelabel, whitelabelConfig, "32x32")}
      />
      <link
        rel="icon"
        type="image/png"
        sizes="96x96"
        href={getFaviconUrl(isWhitelabel, whitelabelConfig, "96x96")}
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href={getFaviconUrl(isWhitelabel, whitelabelConfig, "16x16")}
      />
      <meta
        name="msapplication-TileImage"
        content={
          isWhitelabel && whitelabelConfig?.domain?.fav_icon
            ? getImagePath(whitelabelConfig.domain.fav_icon)
            : `${imageUrl}/ms-icon-144x144.png`
        }
      />
      <meta name="format-detection" content="telephone=no" />

      {/* <!-- Baidu Veification show only on home page --> */}
      {/* {typeof meta == "object" && (
        <meta name="baidu-site-verification" content="codeva-7usbQC7tCs" />
      )} */}

      {/* <!-- Author and Veification --> */}
      <meta name="author" content="Amberstudent" />
      {isWhitelabel && isWhitelabelSeoEnabled && whitelabelGoogleSiteVerificationId ? (
        <meta name="google-site-verification" content={whitelabelGoogleSiteVerificationId} />
      ) : (
        <meta
          name="google-site-verification"
          content="fONjCWN2ez02Ntuu9MImjeS2G9Dd-w09rAanQN_gFNQ"
        />
      )}
      <meta name="chrome" content="nointentdetection" />
      {typeof meta == "object" && meta.preloadImage && !isMiniProgram && !isAppWebView && (
        <link rel="preload" as="image" href={meta.preloadImage} />
      )}

      {/* <!-- Organization Details --> */}
      {!__CLIENT__ && !isWhitelabel && !meta?.excludeSiteSchema && (
        <script type="application/ld+json" nonce={nonce}>
          {JSON.stringify(organizationSchemaData)}
        </script>
      )}

      {/* ms validate meta for china app  */}
      {config.IS_CHINA_APP && (
        <meta name="msvalidate.01" content="FA0EAA5300331A944ABB517B5C779D77" />
      )}

      {/* IOS APP Details  */}
      {!__CLIENT__ && !isWhitelabel && !meta?.excludeSiteSchema && (
        <script type="application/ld+json" nonce={nonce}>
          {JSON.stringify(iosAppSchemaData)}
        </script>
      )}

      {/* Android APP Details  */}
      {!__CLIENT__ && !isWhitelabel && !meta?.excludeSiteSchema && (
        <script type="application/ld+json" nonce={nonce}>
          {JSON.stringify(androidAppSchemaData)}
        </script>
      )}

      {/* <!-- SEO Schema Scripts -->  */}
      {!__CLIENT__ &&
        typeof meta == "object" &&
        meta?.schemaData &&
        meta.schemaData?.map((schema: any, index: number) => (
          <script key={`schema-data-${index}`} type="application/ld+json" nonce={nonce}>
            {JSON.stringify(schema)}
          </script>
        ))}
      {/* <!-- Meta --> */}
      {/* EU language pages always noindex,nofollow (wins over custom robots / pagination tags),
          except production whitelabel homepage which stays indexable via the branches below. */}
      {typeof meta == "object" && forceEuLangNoindex && (
        <meta name="robots" content="noindex,nofollow" />
      )}
      {typeof meta == "object" && !forceEuLangNoindex && meta.robots && (
        <meta name="robots" content={meta.robots} />
      )}
      {typeof meta == "object" &&
        !forceEuLangNoindex &&
        !meta.robots &&
        ((meta.index && !meta.index_tag_disable && indexableDomain) ||
          isIndexableWhitelabelPage) && <meta name="robots" content="index,follow" />}
      {typeof meta == "object" &&
        !forceEuLangNoindex &&
        !meta.robots &&
        ((!meta.index && !meta.index_tag_disable) || !indexableDomain) &&
        !isIndexableWhitelabelPage && <meta name="robots" content="noindex,nofollow" />}
      {typeof meta == "object" && meta.sitemapLink && (
        <link rel="sitemap" type="application/xml" title="Sitemap" href={meta.sitemapLink} />
      )}
      {typeof meta === "object" && meta.canonicalUrl && (
        <link rel="canonical" href={generateCanonicalUrl(meta.canonicalUrl, userLanguage)} />
      )}
      {/* Hreflang temporarily disabled site-wide (including en / x-default)
      {hreflangLinks.map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}
      */}
      {typeof meta == "object" && meta.prevUrl && <link rel="prev" href={meta.prevUrl} />}
      {typeof meta == "object" && meta.nextUrl && <link rel="next" href={meta.nextUrl} />}
      {meta?.title && <title itemProp="name">{meta.title}</title>}
      {meta?.title && <meta itemProp="name" content={meta.title} />}
      {meta?.description && <meta name="description" content={meta.description} />}
      {meta?.description && <meta itemProp="description" content={meta.description} />}
      {typeof meta == "object" && meta.ogTitle && (
        <meta property="og:title" content={meta.ogTitle} />
      )}
      {typeof meta == "object" && meta.ogType && <meta property="og:type" content={meta.ogType} />}
      {typeof meta == "object" && meta.ogImage && (
        <meta
          property="og:image"
          content={
            isWhitelabel && whitelabelConfig?.domain?.fav_icon
              ? getImagePath(whitelabelConfig.domain.fav_icon)
              : meta.ogImage
          }
        />
      )}
      {typeof meta == "object" && meta.ogUrl && <meta property="og:url" content={meta.ogUrl} />}
      {typeof meta == "object" && meta.fbAppId && (
        <meta property="fb:app_id" content={meta.fbAppId} />
      )}
      {typeof meta == "object" && meta.ogSiteName && (
        <meta property="og:site_name" content={meta.ogSiteName} />
      )}
      {typeof meta == "object" && meta.ogDescription && (
        <meta property="og:description" content={meta.ogDescription} />
      )}
      {typeof meta == "object" && meta.twitterUrl && (
        <meta name="twitter:url" content={meta.twitterUrl} />
      )}
      {typeof meta == "object" && meta.twitterTitle && (
        <meta name="twitter:title" content={meta.twitterTitle} />
      )}
      {typeof meta == "object" && meta.twitterDescription && (
        <meta name="twitter:description" content={meta.twitterDescription} />
      )}
      {typeof meta == "object" && meta.twitterImageSrc && (
        <meta name="twitter:image" content={meta.twitterImageSrc} />
      )}
      {typeof meta == "object" && meta.twitterCard && (
        <meta name="twitter:card" content={meta.twitterCard} />
      )}
      {typeof meta == "object" && meta.twitterSite && (
        <meta name="twitter:site" content={meta.twitterSite} />
      )}
      {typeof meta == "object" && meta?.dateModified && (
        <meta itemProp="datemodified" content={meta.dateModified} />
      )}
      {typeof meta == "object" && meta?.dateModified && (
        <time itemProp="datemodified" dateTime={meta.dateModified} />
      )}
    </Helmet>
  );
}
